// Pure helpers for the "AI agent" star and the "Humans only" report filter. No React, no data
// fetching, so they can be unit-tested without a Supabase client.

export interface AiAgentProfile {
  user_id: string;
  name: string;
  avatar_url: string | null;
  job_title: string | null;
  agent_kind: 'voice' | 'chat' | null;
  agent_id: string | null;
  /** Absent until the additive RPC migration is deployed; never infer from a name or login. */
  ziwo_user_id?: string | null;
}

/** The set of profile ids that belong to AI agents. */
export function buildAiAgentIdSet(
  profiles: readonly AiAgentProfile[] | null | undefined,
): ReadonlySet<string> {
  return new Set((Array.isArray(profiles) ? profiles : []).map((p) => p.user_id));
}

/**
 * True only for PostgREST's PGRST202: "this RPC is not in the schema cache" -- i.e. the migration
 * that adds it is not applied yet. Not 42883: that can come from INSIDE an existing RPC, and
 * swallowing it would silently drop the AI star (Codex, 2026-10-06).
 */
export function isMissingRpc(code: string | undefined | null): boolean {
  return code === 'PGRST202';
}

/** True when `userId` is an AI agent's profile id; false for empty ids and humans. */
export function isAiAgentId(ids: ReadonlySet<string>, userId: string | null | undefined): boolean {
  return !!userId && ids.has(userId);
}

/** Provider IDs are a separate namespace from profile/native AI UUIDs. */
export function buildAiZiwoAgentIdSet(
  profiles: readonly AiAgentProfile[] | null | undefined,
): ReadonlySet<string> {
  return new Set(
    (Array.isArray(profiles) ? profiles : []).flatMap((p) =>
      p.agent_kind === 'voice' && p.ziwo_user_id ? [p.ziwo_user_id] : [],
    ),
  );
}

/** Native voice/chat agent IDs, distinct from both profile UUIDs and provider IDs. */
export function buildAiNativeAgentIdSet(
  profiles: readonly AiAgentProfile[] | null | undefined,
): ReadonlySet<string> {
  return new Set(
    (Array.isArray(profiles) ? profiles : []).flatMap((p) => (p.agent_id ? [p.agent_id] : [])),
  );
}

/** Retain unknown identities; callers can recompute totals from the filtered rows. */
export function filterHumans<T>(
  rows: readonly T[],
  getId: (row: T) => string | null | undefined,
  isAi: (userId?: string | null) => boolean | null,
  humansOnly: boolean,
): T[] {
  // Unavailable identities retain all rows; callers must label their totals as unfiltered.
  if (!humansOnly || isAi(null) === null) return [...rows];
  return rows.filter((row) => isAi(getId(row)) !== true);
}
